import ReactPlayer from 'react-player';
import React, { useRef, FC } from 'react';
import { useIsVisible } from '../../../hooks/useVisible';
import defaultStyles from './VideoPlayer.module.scss';
import { useSelector } from 'react-redux';
import mergeClasses from '../../../utils/shallowMerge';

interface VideoPlayerProps {
  url: string;
  customClasses?: Record<string, string>;
  aspectRatio?: string;
  onReady?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  playing?: boolean;
  playsinline?: boolean;
  controls?: boolean;
  loop?: boolean;
  muted?: boolean;
  width?: string | number;
  height?: string | number;
  customControlsComponent?: React.ReactNode | null;
}

interface RootState {
  renderContext: {
    serverSideRendering: boolean;
  };
}

const VideoPlayer: FC<VideoPlayerProps> = ({
  url,
  customClasses,
  aspectRatio,
  onReady,
  onPlay,
  onPause,
  playing = true,
  playsinline = true,
  controls = false,
  loop = true,
  muted = true,
  width = '100%',
  height = '100%',
  customControlsComponent = null,
}) => {
  if (!url) return null;

  const isSSR = useSelector((state: RootState) => state.renderContext.serverSideRendering);
  const videoRef = useRef<HTMLDivElement>(null);
  const isVisible = useIsVisible(videoRef, {}, isSSR);
  const styles = mergeClasses(defaultStyles, customClasses);

  return (
    <div className={styles.wrapper} ref={videoRef} style={{ aspectRatio: aspectRatio }}>
      <ReactPlayer
        url={url}
        style={{ aspectRatio: aspectRatio, ...styles }}
        playing={isVisible && playing}
        loop={loop}
        playsinline={playsinline}
        controls={controls}
        muted={muted}
        width={width}
        height={height}
        onReady={onReady}
        onPlay={onPlay}
        onPause={() => {
          if (isVisible) {
            onPause && onPause();
          }
        }}
      />
      {customControlsComponent}
    </div>
  );
};

export default VideoPlayer;
